import type { Config } from "tailwindcss";
import { colors, fonts, radius } from "./theme.js";

/**
 * Shared Tailwind preset. Every app's tailwind.config consumes this via `presets`,
 * so brand colors/spacing live in exactly one place (theme.ts).
 */
export const preset = {
  theme: {
    extend: {
      colors: {
        bg: colors.bg,
        surface: colors.surface,
        "surface-raised": colors.surfaceRaised,
        border: colors.border,
        accent: {
          DEFAULT: colors.accent,
          muted: colors.accentMuted,
        },
        text: {
          DEFAULT: colors.text,
          muted: colors.textMuted,
          faint: colors.textFaint,
        },
        gold: colors.gold,
        silver: colors.silver,
        bronze: colors.bronze,
        win: colors.win,
        danger: colors.danger,
      },
      fontFamily: {
        sans: fonts.sans.split(",").map((f) => f.trim()),
        mono: fonts.mono.split(",").map((f) => f.trim()),
        display: ["Azeret Mono", "ui-monospace", "SF Mono", "Menlo", "monospace"],
      },
      borderRadius: {
        sm: radius.sm,
        md: radius.md,
        lg: radius.lg,
      },
      boxShadow: {
        glow: `0 0 40px ${colors.accentGlow}`,
        "glow-win": `0 0 40px ${colors.winGlow}`,
        "glow-gold": `0 0 48px ${colors.goldGlow}`,
        "glow-danger": `0 0 48px ${colors.dangerGlow}`,
      },
      keyframes: {
        // early-tap penalty
        shake: {
          "0%, 100%": { transform: "translateX(0)" },
          "20%, 60%": { transform: "translateX(-8px)" },
          "40%, 80%": { transform: "translateX(8px)" },
        },
        // result / new-best reveal
        pop: {
          "0%": { transform: "scale(0.8)", opacity: "0" },
          "60%": { transform: "scale(1.06)", opacity: "1" },
          "100%": { transform: "scale(1)", opacity: "1" },
        },
        // new personal best — pulsing pink glow
        glow: {
          "0%, 100%": { textShadow: `0 0 0 ${colors.accentGlow}` },
          "50%": { textShadow: `0 0 28px ${colors.accent}` },
        },
        // ambient tension while waiting for green
        breathe: {
          "0%, 100%": { opacity: "0.55" },
          "50%": { opacity: "1" },
        },
        // full-surface win flash (green) — one beat, not a strobe
        flashWin: {
          "0%": { backgroundColor: "rgba(0, 232, 123, 0.18)" },
          "100%": { backgroundColor: "transparent" },
        },
        // full-surface bust flash (red)
        flashBust: {
          "0%": { backgroundColor: "rgba(255, 51, 85, 0.22)" },
          "100%": { backgroundColor: "transparent" },
        },
        // expanding ring burst on a landed win
        burst: {
          "0%": { transform: "scale(0.6)", opacity: "0.9" },
          "100%": { transform: "scale(2.2)", opacity: "0" },
        },
        // the primary CTA heartbeat — slow, expensive-feeling
        neonPulse: {
          "0%, 100%": { boxShadow: `0 0 18px ${colors.accentGlow}` },
          "50%": { boxShadow: `0 0 44px ${colors.accent}` },
        },
        // gold shimmer sweep across jackpot text
        shimmer: {
          "0%": { backgroundPosition: "-200% 0" },
          "100%": { backgroundPosition: "200% 0" },
        },
        // staggered page-load reveal (pair with per-item animation-delay)
        fadeUp: {
          "0%": { transform: "translateY(14px)", opacity: "0" },
          "100%": { transform: "translateY(0)", opacity: "1" },
        },
        // ambient drift for decorative elements (orbs, motifs)
        float: {
          "0%, 100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(-8px)" },
        },
        // marquee ticker — content must be duplicated once for a seamless loop
        ticker: {
          "0%": { transform: "translateX(0)" },
          "100%": { transform: "translateX(-50%)" },
        },
        // slow rotation for background wheels/rings
        spinSlow: {
          "0%": { transform: "rotate(0deg)" },
          "100%": { transform: "rotate(360deg)" },
        },
      },
      animation: {
        shake: "shake 0.4s ease-in-out",
        pop: "pop 0.35s cubic-bezier(0.34,1.56,0.64,1)",
        glow: "glow 1.2s ease-in-out infinite",
        breathe: "breathe 1.4s ease-in-out infinite",
        "flash-win": "flashWin 0.7s ease-out",
        "flash-bust": "flashBust 0.7s ease-out",
        burst: "burst 0.6s ease-out forwards",
        "neon-pulse": "neonPulse 2.2s ease-in-out infinite",
        shimmer: "shimmer 2.4s linear infinite",
        "fade-up": "fadeUp 0.5s ease-out both",
        float: "float 6s ease-in-out infinite",
        ticker: "ticker 32s linear infinite",
        "spin-slow": "spinSlow 48s linear infinite",
      },
    },
  },
} satisfies Partial<Config>;

export default preset;
